import {Component, effect, ElementRef, inject, input, signal, viewChild} from '@angular/core';
import {LucideAngularModule, SaveIcon, SquarePlusIcon, Trash2Icon} from 'lucide-angular';
import {FormBuilder, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {ActivityTagDTO} from '@domain/activity-tag/activity-tag.dto';
import {
  ActivityTagFieldComponent
} from '@domain/activity-tag/components/form/activity-tag-field/activity-tag-field.component';
import {ActivityDTO} from '@domain/activity/activity.dto';
import {ActivityApi} from '@domain/activity/activity.api';

@Component({
  selector: 'app-activity-tag-manager',
  imports: [
    LucideAngularModule,
    FormsModule,
    ReactiveFormsModule,
    ActivityTagFieldComponent
  ],
  styleUrl: 'activity-tag-manager.component.scss',
  templateUrl: './activity-tag-manager.component.html'
})
export class ActivityTagManagerComponent {

  public readonly addTagDialog = viewChild.required<ElementRef<HTMLDialogElement>>('addTagDialog');
  public readonly removeTagDialog = viewChild.required<ElementRef<HTMLDialogElement>>('removeTagDialog');

  public readonly activity = input.required<ActivityDTO>();
  public readonly canModify = input(false);

  protected readonly tags = signal<ActivityTagDTO[] | null>(null);
  protected readonly tagToRemove = signal<Partial<ActivityTagDTO> | null>(null);

  public readonly icons = {
    save: SaveIcon,
    plus: SquarePlusIcon,
    delete: Trash2Icon
  };

  private readonly activityApi = inject(ActivityApi);
  private readonly fb = inject(FormBuilder);
  protected readonly addTagForm = this.fb.group({
    tag: this.fb.control<ActivityTagDTO | null>(null, Validators.required),
  });

  constructor() {
    effect(() => {
      this.tags.set(this.activity()?.tags ?? []);
    });
  }

  protected showAddTag(): void {
    this.addTagForm.reset();
    this.addTagDialog().nativeElement.showModal();
  }

  protected closeAddTag(): void {
    this.addTagDialog().nativeElement.close();
  }

  protected addTag(): void {
    const activityId = this.activity().id;
    const tagId = this.addTagForm?.controls.tag.value?.id;
    if (!(activityId && tagId && this.addTagForm.valid)) {
      return;
    }
    this.activityApi.addTag(activityId, tagId).subscribe(refreshedActivity => {
      this.tags.set(refreshedActivity?.tags ?? []);
      this.closeAddTag();
    });
  }

  protected showRemoveTag(tagToRemove: Partial<ActivityTagDTO>): void {
    this.tagToRemove.set(tagToRemove);
    this.removeTagDialog().nativeElement.showModal();
  }

  protected closeRemoveTag(): void {
    this.tagToRemove.set(null);
    this.removeTagDialog().nativeElement.close();
  }

  protected removeTag(): void {
    const activityId = this.activity().id;
    const tagId = this.tagToRemove()?.id;
    if (!(activityId && tagId)) {
      return;
    }
    this.activityApi.removeTag(activityId, tagId).subscribe(refreshedActivity => {
      this.tags.set(refreshedActivity?.tags ?? []);
      this.closeRemoveTag();
    });
  }

}
